import React from 'react'

import { Chip, makeStyles } from '@material-ui/core'

import { formatDate } from '../InternalInvoice/InvoiceDetailModal/helpers'
import { BillingGroup } from '../../models/Billing/types'
import { InvoiceFilters } from './components/FilterDrawer'

const useStyles = makeStyles((theme) => ({
    chipContainer: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: theme.spacing(1),
    },
    chip: {
        margin: theme.spacing(0.5),
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
        },
    },
}))

interface FilterChipsProps {
    filters: InvoiceFilters
    onDelete: (filter: keyof InvoiceFilters) => void
    appliedFilters?: InvoiceFilters
    billingGroups?: BillingGroup[] | null
}

export const FilterChips: React.FC<FilterChipsProps> = ({
    filters,
    onDelete,
    appliedFilters,
    billingGroups,
}) => {
    const classes = useStyles()

    const getFilterLabel = (key: keyof InvoiceFilters, value: any): string => {
        const operatorSymbol = {
            eq: '=',
            gt: '>',
            lt: '<',
            gte: '≥',
            lte: '≤',
        }

        const getBillingGroupDisplayName = (billingGroupId: number) => {
            if (!billingGroups) return ''
            const billingGroup = billingGroups.find(
                (bg: BillingGroup) => bg.id === billingGroupId,
            )
            return billingGroup?.quickbooks_customer?.display_name || ''
        }

        switch (key) {
            case 'invoiceNumber':
                return `Invoice: ${value}`
            case 'status':
                return `Status: ${(value as string[]).join(', ')}`
            case 'startDate':
                return `From: ${formatDate(value)}`
            case 'endDate':
                return `To: ${formatDate(value)}`
            case 'company':
                return `Company ID: ${value}`
            case 'billingGroup':
                return `Billing Group: ${getBillingGroupDisplayName(value)}`
            default:
                return `${key}: ${value}`
        }
    }

    const renderChips = () => {
        const chips: JSX.Element[] = []

        Object.entries(filters).forEach(([key, value]) => {
            if (value !== undefined && value !== null && value !== '') {
                if (Array.isArray(value) && value.length > 0) {
                    chips.push(
                        <Chip
                            key={key}
                            label={getFilterLabel(
                                key as keyof InvoiceFilters,
                                value,
                            )}
                            onDelete={() =>
                                onDelete(key as keyof InvoiceFilters)
                            }
                            className={classes.chip}
                        />,
                    )
                } else if (!Array.isArray(value)) {
                    chips.push(
                        <Chip
                            key={key}
                            label={getFilterLabel(
                                key as keyof InvoiceFilters,
                                value,
                            )}
                            onDelete={() =>
                                onDelete(key as keyof InvoiceFilters)
                            }
                            className={classes.chip}
                        />,
                    )
                }
            }
        })

        return chips
    }

    return <div className={classes.chipContainer}>{renderChips()}</div>
}
